/**
 * Team Users and Fanatics → site-wide table export sources. Both lists are
 * server-paged; the export reads every page of the current search / filter /
 * sort so the file holds the whole list the admin is looking at.
 */
import type { ApiClient } from '../../lib/api-client';
import type {
  FanaticGrantStatusFilter,
  TeamFanaticGrant,
  TeamFanaticGrantSortField,
  TeamUser,
} from '../../lib/api-types';
import { PERSONA_LABELS } from '../../lib/personas';
import type { ExportCell, ExportColumnDef, TableExportSource } from '../integrations/export-dialog';
import { exportFileStem, fetchAllPages, fileDay } from '../integrations/export-format';

import type { TeamUsersQueryState } from './use-team-users';

const USER_COLUMNS: readonly ExportColumnDef[] = [
  { field: 'email', label: 'Email', isVisible: true },
  { field: 'display_name', label: 'Name', isVisible: true },
  { field: 'persona', label: 'Persona', isVisible: true },
  { field: 'mfa', label: 'MFA', isVisible: true },
  { field: 'status', label: 'Status', isVisible: true },
  { field: 'last_login_at', label: 'Last sign-in', isVisible: true },
  { field: 'created_at', label: 'Added', isVisible: false },
];

function day(iso: string | null): string | null {
  return iso === null || iso === '' ? null : fileDay(iso);
}

export function teamUserRowCells(user: TeamUser): ExportCell[] {
  return [
    user.email,
    user.display_name,
    user.persona === null ? null : PERSONA_LABELS[user.persona],
    user.mfa_enabled ? 'Enabled' : user.mfa_enforced ? 'Required — not set up' : 'Off',
    !user.active ? 'Removed' : user.invite_pending ? 'Invite pending' : 'Active',
    day(user.last_login_at),
    day(user.created_at),
  ];
}

export async function buildTeamUsersExportSource(
  api: ApiClient,
  query: TeamUsersQueryState,
  teamName: string | null
): Promise<TableExportSource> {
  const users = await fetchAllPages(
    (page, limit) =>
      api.listTeamUsers({
        q: query.search === '' ? undefined : query.search,
        persona: query.persona === 'all' ? undefined : query.persona,
        sort_by: query.sortBy,
        sort_order: query.sortOrder,
        page,
        limit,
        include_inactive: query.isShowingRemoved ? true : undefined,
      }),
    100
  );
  return {
    page: 'team_users',
    pageLabel: 'Team Users',
    columns: USER_COLUMNS,
    rows: () => users.map(teamUserRowCells),
    fileStem: exportFileStem('TeamUsers', teamName, fileDay(null)),
    sheetName: 'Team Users',
    rowNoun: { one: 'user', many: 'users' },
  };
}

const FANATIC_COLUMNS: readonly ExportColumnDef[] = [
  { field: 'email', label: 'Email', isVisible: true },
  { field: 'display_name', label: 'Name', isVisible: true },
  { field: 'status', label: 'Status', isVisible: true },
  { field: 'granted_at', label: 'Following since', isVisible: true },
  { field: 'invite_expires_at', label: 'Invite expires', isVisible: false },
  { field: 'revoked_at', label: 'Revoked', isVisible: false },
  { field: 'revoked_reason', label: 'Revoke reason', isVisible: false },
];

const GRANT_STATUS_LABELS: Readonly<Record<string, string>> = {
  active: 'Active',
  pending_invite: 'Invite pending',
  revoked: 'Revoked',
};

export function fanaticRowCells(grant: TeamFanaticGrant): ExportCell[] {
  return [
    grant.fanatic_email ?? grant.invite_email,
    grant.fanatic_display_name,
    GRANT_STATUS_LABELS[grant.status] ?? grant.status,
    day(grant.accepted_at ?? grant.granted_at),
    day(grant.invite_expires_at),
    day(grant.revoked_at),
    grant.revoked_reason,
  ];
}

export async function buildFanaticsExportSource(
  api: ApiClient,
  tenantId: string,
  query: {
    q: string;
    status: FanaticGrantStatusFilter;
    sort: TeamFanaticGrantSortField;
    direction: 'asc' | 'desc';
  }
): Promise<TableExportSource> {
  const grants = await fetchAllPages(
    (page, limit) =>
      api
        .listTeamFanaticGrants(tenantId, {
          q: query.q === '' ? undefined : query.q,
          status: query.status,
          sort: query.sort,
          direction: query.direction,
          page,
          page_size: limit,
        })
        .then((result) => ({ items: result.grants, total: result.pagination.total })),
    100
  );
  return {
    page: 'fanatics',
    pageLabel: 'Fanatics',
    columns: FANATIC_COLUMNS,
    rows: () => grants.map(fanaticRowCells),
    fileStem: exportFileStem('Fanatics', null, fileDay(null)),
    sheetName: 'Fanatics',
    rowNoun: { one: 'follower', many: 'followers' },
  };
}
